import React, { useContext, useState, useEffect } from 'react';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { hasDuplicates } from '@mummssoftware/utils/hcpcsUtils';
import {
  HCPCSGlobalContext,
  SET_SUBMIT_HCPCS_ERROR,
  SET_ICD_SECTION,
  RECORD_DATA,
} from '@mummssoftware/global-context/HCPCSGlobalContext';
import { ActionIcon } from '@mummssoftware/common-ui';
import { PalliativeCare } from '@mummssoftware/utils/constants';
import { ErrorText } from '@mummssoftware/common-ui/style';
import { ICDCode } from './ICDCode';
import { AppContext } from '../../../../context';
import { AddICDDiv, ICDContainer, ICDTitle } from './HCPCSWrapper';
import { iconStyle } from './constants';
import defaults from '../../../../translations/en.json';

type AddICDCodeProps = {
  hcpcsChoice: mumms.HCPCSMenuItem;
};

export const AddICDCode = ({ hcpcsChoice }: AddICDCodeProps) => {
  const {
    hcpcsDuration,
    icdSection,
    icdCodeNames,
    dispatchHCPCS,
    icdCodes,
  } = useContext(HCPCSGlobalContext);
  const { patient } = useContext(AppContext);
  // console.log('icdSection', icdSection);
  const icdCodesArray = icdSection[hcpcsChoice.key] || [];
  const icdCodesArrayMax = icdCodesArray.length
    ? icdCodesArray.filter((x: any) => !x.hide)
    : [];
  const maxICDCodes = 4;
  const icdCodesNameArray = icdCodeNames[hcpcsChoice.key];
  const [hasICDduplicate, setDuplicate] = useState<boolean>(false);
  const [plusDisabled, setPlusDisable] = useState<boolean>(false);
  const showICDCodeMockUp =
    (hcpcsDuration && hcpcsDuration[hcpcsChoice.key]) || false;

  const isPalCarePatient = patient?.programSiteName === PalliativeCare;
  const [showPalCareError, setPalCareError] = useState<boolean>(
    isPalCarePatient,
  );

  /**
   * Logic for the Plus ICD to show or not
   */
  useEffect(() => {
    if (icdCodesArrayMax.length === maxICDCodes) {
      setPlusDisable(true);
    } else if (
      !Object.keys(icdSection).length &&
      !Object.keys(icdCodeNames).length
    ) {
      // step 1: no icd code entered
      setPlusDisable(false);
      // step 2: icd section but no code entered
    } else if (
      Object.keys(icdSection).length &&
      !Object.keys(icdCodeNames).length
    ) {
      if (icdSection[hcpcsChoice.key]) {
        const icdArr = icdSection[hcpcsChoice.key].filter((x) => !x.hide);
        if (icdArr.length) {
          setPlusDisable(true);
        } else {
          setPlusDisable(false);
        }
      } else {
        setPlusDisable(false);
      }
      // step 3
      // ICD selected
      // is there a search box opened and no ICD code selected
    } else if (
      Object.keys(icdSection).length &&
      Object.keys(icdCodeNames).length
    ) {
      if (icdSection[hcpcsChoice.key] && icdCodeNames[hcpcsChoice.key]) {
        const icdArr = icdSection[hcpcsChoice.key].filter((x) => !x.hide);
        if (icdArr.length === icdCodeNames[hcpcsChoice.key].length) {
          setPlusDisable(false);
        } else {
          setPlusDisable(true);
        }
      } else if (
        icdSection[hcpcsChoice.key] &&
        !icdCodeNames[hcpcsChoice.key]
      ) {
        const icdArr = icdSection[hcpcsChoice.key].filter((x) => !x.hide);
        if (icdArr.length) {
          setPlusDisable(true);
        } else {
          setPlusDisable(false);
        }
      }
    }
  }, [
    icdSection,
    icdCodeNames,
    JSON.stringify(icdSection),
    JSON.stringify(icdCodeNames),
  ]);

  /**
   * Logic for duplicate
   */
  useEffect(() => {
    if (icdCodeNames[hcpcsChoice.key]) {
      const hasduplicate =
        icdCodesNameArray && hasDuplicates(icdCodesNameArray);
      if (hasduplicate) {
        setDuplicate(true);
        setPlusDisable(true);
        dispatchHCPCS({
          type: SET_SUBMIT_HCPCS_ERROR,
          payload: true,
        });
      }
      if (hasICDduplicate && !hasduplicate) {
        setDuplicate(false);
        dispatchHCPCS({
          type: SET_SUBMIT_HCPCS_ERROR,
          payload: false,
        });
      }
    }
  }, [
    icdCodeNames &&
      icdCodeNames[hcpcsChoice.key] &&
      icdCodeNames[hcpcsChoice.key].length,
  ]);

  useEffect(() => {
    if (isPalCarePatient) {
      const hasIcdCode =
        icdCodes &&
        icdCodes[hcpcsChoice.key] &&
        Object.keys(icdCodes[hcpcsChoice.key]).length;

      if (hasIcdCode) {
        setPalCareError(false);
        dispatchHCPCS({
          type: SET_SUBMIT_HCPCS_ERROR,
          payload: false,
        });
      } else {
        setPalCareError(true);
        dispatchHCPCS({
          type: SET_SUBMIT_HCPCS_ERROR,
          payload: true,
        });
      }
    }
  }, [icdCodes && JSON.stringify(icdCodes)]);

  const onAddICD = () => {
    dispatchHCPCS({
      type: RECORD_DATA,
      payload: true,
    });
    dispatchHCPCS({
      type: SET_ICD_SECTION,
      payload: {
        key: hcpcsChoice.key,
        codes: [...icdCodesArray, hcpcsChoice],
      },
    });
  };

  const getICDSection = () => {
    if (icdCodesArray.length) {
      const icdsection = icdCodesArray.map(
        (x: mumms.HCPCSMenuItem, i: number) => (
          // eslint-disable-next-line react/no-array-index-key
          <ICDCode hcpcsChoice={x} index={i} key={`${x.key}-${i}`} />
        ),
      );
      return <ICDContainer id="icd-container">{icdsection}</ICDContainer>;
    }
    return <></>;
  };

  return showICDCodeMockUp ? (
    <AddICDDiv id="icd-codes">
      <ICDTitle id="icd-title">
        {defaults.icd}
        <ActionIcon
          style={iconStyle}
          Icon={AddCircleIcon}
          onClick={onAddICD}
          disabled={plusDisabled}
        />
        {hasICDduplicate && (
          <ErrorText>
            <ErrorIcon />
            {defaults.icdDup}
          </ErrorText>
        )}
        {showPalCareError && (
          <ErrorText>
            <ErrorIcon />
            {defaults.palCarePatient}
          </ErrorText>
        )}
      </ICDTitle>

      {getICDSection()}
    </AddICDDiv>
  ) : (
    <></>
  );
};
